import pluralize from 'pluralize';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useTrackRemovedAnAssetFromCollectionForm } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { type Clip } from '@air/api/types';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import {
  contentCollectionFormSelector,
  contentCollectionSessionIdSelector,
  deleteContentCollectionClipsAction,
} from '@air/feature-content-collection/redux';
import { useShortIdContext } from '@air/provider-short-id';
import { useToasts } from '@air/provider-toast';
import { removeUploadsAction, uploadArraySelector } from '@air/redux-uploader';

import { DeleteAssetsWarning } from '~/components/Modals/DeleteBoards/DeleteAssetsWarning';
import { useAirStore } from '~/utils/ReduxUtils';

export type DeletePrivateClipsModalProps = {
  clips: Pick<Clip, 'id' | 'assetId'>[];
};

export const ContentCollectionDeleteClipsModal = ({ clips, onClose }: AirModalProps<DeletePrivateClipsModalProps>) => {
  const store = useAirStore();
  const { trackRemovedAnAssetFromCollectionForm } = useTrackRemovedAnAssetFromCollectionForm();
  const dispatch = useDispatch();
  const [deleting, setDeleting] = useState(false);
  const { showToast } = useToasts();
  const { shortId } = useShortIdContext();

  const deleteClips = async () => {
    try {
      setDeleting(true);
      const uploads = uploadArraySelector(store.getState());
      const uploadIds = uploads.filter(({ clipId }) => clips.find(({ id }) => id === clipId)).map(({ id }) => id);
      const sessionId = contentCollectionSessionIdSelector(store.getState());
      const form = contentCollectionFormSelector(store.getState());

      if (!sessionId) {
        return;
      }

      dispatch(
        removeUploadsAction({
          uploadIds,
        }),
      );

      dispatch(deleteContentCollectionClipsAction({ clipIds: clips.map((c) => c.id) }));

      await ShortUrl.deleteFormSessionAssets({
        assetIds: clips.map((clip) => clip.assetId),
        formId: form.id,
        sessionId,
        shortId,
      });

      trackRemovedAnAssetFromCollectionForm({ numberOfAssets: clips?.length });
      onClose();
    } catch (_error) {
      showToast(`There was an error. Please try again later.`);

      setDeleting(false);
    }
  };

  return (
    <ConfirmationModal
      id="delete-board-collection-clips"
      isOpen
      data-testid="BOARD_COLLECTION_DELETE_CLIPS_MODAL"
      onDismiss={onClose}
      title={`Delete ${pluralize('item', clips.length, true)}?`}
      description={`${pluralize('This', clips.length)} ${pluralize(
        'item',
        clips.length,
      )} will be removed from your content submission unless you re-upload ${clips.length > 1 ? 'them' : 'it'}.`}
      onConfirm={deleteClips}
      ctaPrimaryText={deleting ? 'Deleting…' : 'Delete'}
      ctaSecondaryText="Cancel"
      isDangerous
      isLoading={deleting}
    >
      <div className="mt-4">
        <DeleteAssetsWarning />
      </div>
    </ConfirmationModal>
  );
};
